import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  FormControl,
} from "@mui/material";
import FaqModal from "./FaqModal";
import ConfirmationModal from "./ConfirmationModal";
import {
  useCreateNewFaqsMutation,
  useDeleteFaqMutation,
  useNewFaqsAfterMutation,
  useUpdateFaqByIdMutation,
} from "../../../Services/faqs/FAQs";
import { toast } from "react-toastify";
import "./FaqList.scss";

interface FaqListProps {
  faqs: any[];
  refetchFaqs: () => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const FaqList: React.FC<FaqListProps> = ({
  faqs,
  refetchFaqs,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFaqId, setSelectedFaqId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<number | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "insert">("add");
  const [currentFaq, setCurrentFaq] = useState({
    question: "",
    answer: "",
    id: null,
    insert_after_id: null,
  });

  // API Mutations
  const [triggerCreateNewFaqs, { isLoading: isCreating }] =
    useCreateNewFaqsMutation({});

  const [triggerNewFaqsByOrder, { isLoading: isOrderUpdating }] =
    useNewFaqsAfterMutation({});

  const [triggerDeleteFaq, { isLoading: isDeleting }] = useDeleteFaqMutation(
    {}
  );

  const [triggerUpdateFaqById, { isLoading: isUpdatingById }] =
    useUpdateFaqByIdMutation({});

  // Combined loading state
  const isLoading =
    isCreating || isOrderUpdating || isUpdatingById || isDeleting;

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  const handleMenuOpen = (event, faqId) => {
    setAnchorEl(event.currentTarget);
    setSelectedFaqId(faqId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFaqId(null);
  };

  // Handle Add New FAQ
  const handleAddNew = () => {
    console.log("=== ADD NEW FAQ INITIATED ===");
    setModalMode("add");
    setCurrentFaq({
      question: "",
      answer: "",
      id: null,
      insert_after_id: null,
    });
    setShowModal(true);
  };

  // Handle Edit FAQ
  const handleEdit = () => {
    const faq = faqs.find((f) => f.id === selectedFaqId);
    console.log("=== EDIT FAQ INITIATED ===");
    console.log("Selected FAQ for editing:", faq);
    setModalMode("edit");
    setCurrentFaq({ ...faq });
    setShowModal(true);
    handleMenuClose();
  };

  // Handle Delete FAQ - Show Confirmation
  const handleDeleteClick = () => {
    console.log("=== DELETE FAQ INITIATED ===");
    console.log("FAQ ID to delete:", selectedFaqId);
    setFaqToDelete(selectedFaqId);
    setShowDeleteModal(true);
    handleMenuClose();
  };

  // Confirm Delete FAQ
  const handleConfirmDelete = async () => {
    if (!faqToDelete) return;

    try {
      await triggerDeleteFaq({
        id: faqToDelete,
        body: {},
      }).unwrap();

      console.log("FAQ deleted successfully");
      toast.success("FAQ deleted successfully");
      await refetchFaqs();
      setShowDeleteModal(false);
      setFaqToDelete(null);
    } catch (error) {
      console.error("Delete FAQ error:", error);
      toast.error("Failed to delete FAQ");
    }
  };

  // Cancel Delete
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setFaqToDelete(null);
  };

  // Handle Add After FAQ
  const handleAddAfter = () => {
    console.log("=== ADD FAQ AFTER INITIATED ===");
    console.log("Selected FAQ ID:", selectedFaqId);

    const selectedFaq = faqs.find((f) => f.id === selectedFaqId);
    console.log("Selected FAQ:", selectedFaq);

    setModalMode("insert");
    setCurrentFaq({
      question: "",
      answer: "",
      id: null,
      insert_after_id: selectedFaqId,
    });
    console.log("New FAQ will be inserted after ID:", selectedFaqId);
    setShowModal(true);
    handleMenuClose();
  };

  // Submit FAQ (Add/Edit/Insert)
  const handleSubmit = async () => {
    console.log("=== SUBMIT FAQ ===");
    console.log("Modal Mode:", modalMode);
    console.log("Current FAQ Data:", currentFaq);

    if (!currentFaq.question.trim() || !currentFaq.answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    try {
      if (modalMode === "edit") {
        console.log("--- EDIT MODE ---");
        console.log("Updating FAQ with ID:", currentFaq.id);

        await triggerUpdateFaqById({
          id: currentFaq.id,
          body: {
            question: currentFaq.question,
            answer: currentFaq.answer,
          },
        }).unwrap();

        console.log("FAQ Updated Successfully");
        toast.success("FAQ updated successfully");
      } else if (modalMode === "add") {
        console.log("--- ADD MODE ---");

        await triggerCreateNewFaqs({
          body: {
            question: currentFaq.question,
            answer: currentFaq.answer,
          },
        }).unwrap();

        console.log("New FAQ added successfully");
        toast.success("FAQ added successfully");
      } else if (modalMode === "insert") {
        console.log("--- INSERT MODE ---");
        console.log("Insert After ID:", currentFaq.insert_after_id);

        await triggerNewFaqsByOrder({
          body: {
            question: currentFaq.question,
            answer: currentFaq.answer,
            insert_after_id: currentFaq.insert_after_id,
          },
        }).unwrap();

        console.log("FAQ inserted successfully");
        toast.success("FAQ inserted successfully");
      }

      // Refetch FAQs to get updated list
      await refetchFaqs();

      // Close modal and reset form
      setShowModal(false);
      setCurrentFaq({
        question: "",
        answer: "",
        id: null,
        insert_after_id: null,
      });
    } catch (error) {
      console.error("Submit FAQ error:", error);
      toast.error("Failed to save FAQ");
    }

    console.log("=== SUBMIT COMPLETE ===\n");
  };

  // Handle FAQ field changes
  const handleFaqChange = (field: string, value: string) => {
    setCurrentFaq({ ...currentFaq, [field]: value });
  };

  // Handle pagination
  const handlePageChangeCustom = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  return (
    <div className="workshops-container">
      <Box sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography color="#666666" fontSize="1.1rem" fontFamily="Karbon">
            Total FAQs: {totalCount}
          </Typography>
          <Button
            variant="contained"
            component="label"
            startIcon={
              <i className="bi bi-plus-lg" style={{ color: "#ffffff" }}></i>
            }
            onClick={handleAddNew}
            disabled={isLoading}
            sx={{
              bgcolor: "#ff6b35",
              color: "#fff",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              fontFamily: "Karbon",
              "&:hover": {
                bgcolor: "#ff5722",
              },
              "&:disabled": {
                bgcolor: "#ffc7b3",
              },
            }}
          >
            Add New FAQ
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "12px",
            border: "1px solid #f0e5e0",
            maxHeight: "calc(100vh - 320px)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 80,
                    bgcolor: "#f7d3c5",
                    color: "#666666",
                    fontSize: "0.95rem",
                    fontFamily: "Karbon",
                  }}
                >
                  Sr.No.
                </TableCell>
                <TableCell
                  sx={{
                    width: "35%",
                    bgcolor: "#f7d3c5",
                    color: "#666666",
                    fontSize: "0.95rem",
                    fontFamily: "Karbon",
                  }}
                >
                  Question
                </TableCell>
                <TableCell
                  sx={{
                    width: "50%",
                    bgcolor: "#f7d3c5",
                    color: "#666666",
                    fontSize: "0.95rem",
                    fontFamily: "Karbon",
                  }}
                >
                  Answer
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    bgcolor: "#f7d3c5",
                    color: "#666666",
                    fontSize: "0.95rem",
                    textAlign: "center",
                    fontFamily: "Karbon",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faqs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 8 }}>
                    <Typography
                      color="#666666"
                      fontSize="1.1rem"
                      mb={2}
                      fontFamily="Karbon"
                    >
                      No FAQs yet. Add your first FAQ!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                faqs.map((faq, index) => (
                  <TableRow
                    key={faq.id}
                    hover
                    className={index % 2 === 1 ? "row-alternate" : "row-normal"}
                    sx={{
                      "&:hover": {
                        bgcolor: "#fff5f2",
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={(page - 1) * pageSize + index + 1}
                        size="small"
                        sx={{
                          bgcolor: "#f7d3c5",
                          color: "#ff6b35",
                          fontFamily: "Karbon",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography color="#666666" fontFamily="Karbon">
                        {faq.question}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="#666666"
                        sx={{ lineHeight: 1.6 }}
                        fontFamily="Karbon"
                      >
                        {faq.answer}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, faq.id)}
                        size="small"
                        sx={{ color: "#666666" }}
                        disabled={isLoading}
                      >
                        <i className="bi bi-three-dots-vertical"></i>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Custom Pagination */}
        <div className="pagination-footer">
          <div className="pagination-info">
            <div
              className="rows-per-page"
              style={{ fontFamily: "karbon", color: "#666666" }}
            >
              <label htmlFor="rowsPerPage">Rows per page:</label>
              <select
                id="rowsPerPage"
                value={pageSize}
                onChange={handleRowsPerPageChange}
                className="rows-select mx-2"
                style={{
                  fontFamily: "karbon",
                  color: "#666666",
                  outlineColor: "#666666",
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="pagination-controls">
            <span
              className="mx-2"
              style={{ fontFamily: "karbon", color: "#666666" }}
            >
              Showing {startIndex + 1} - {endIndex} of {totalCount}
            </span>
            <button
              onClick={() => handlePageChangeCustom(page - 1)}
              disabled={page === 1}
              className="pagination-btn"
            >
              Prev
            </button>

            {/* Always show exactly 5 page numbers, centered around current page */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Calculate the first page to show (ensuring we always show 5 if possible)
              let firstPage = Math.max(
                1,
                Math.min(
                  page - 2, // Try to center current page
                  totalPages - 4 // But don't go past total pages - 4
                )
              );

              const pageNum = firstPage + i;

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChangeCustom(pageNum)}
                  className={`pagination-btn ${
                    pageNum === page ? "active" : ""
                  }`}
                  style={{
                    display: pageNum > totalPages ? "none" : "inline-block",
                    margin: "0 2px",
                    minWidth: "32px",
                    textAlign: "center",
                  }}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChangeCustom(page + 1)}
              disabled={page === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              fontFamily: "Karbon",
            },
          }}
        >
          <MenuItem
            onClick={handleEdit}
            disabled={isLoading}
            sx={{ fontFamily: "Karbon", color: "#666666" }}
          >
            <i
              className="bi bi-pencil"
              style={{ marginRight: 16, color: "#ff6b35" }}
            ></i>
            Edit
          </MenuItem>
          <MenuItem
            onClick={handleAddAfter}
            disabled={isLoading}
            sx={{ fontFamily: "Karbon", color: "#666666" }}
          >
            <i
              className="bi bi-plus-lg"
              style={{ marginRight: 16, color: "#ff6b35" }}
            ></i>
            Add FAQ After
          </MenuItem>
          <MenuItem
            onClick={handleDeleteClick}
            disabled={isLoading}
            sx={{ fontFamily: "Karbon", color: "#666666" }}
          >
            <i
              className="bi bi-trash"
              style={{ marginRight: 16, color: "#ff6b35" }}
            ></i>
            Delete
          </MenuItem>
        </Menu>

        {/* FAQ Modal */}
        <FaqModal
          open={showModal}
          mode={modalMode}
          faq={currentFaq}
          faqs={faqs}
          onClose={() => !isLoading && setShowModal(false)}
          onSubmit={handleSubmit}
          onChange={handleFaqChange}
          isLoading={isLoading}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          open={showDeleteModal}
          title="Delete FAQ"
          message="Are you sure you want to delete this FAQ? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isLoading={isDeleting}
          confirmText="Delete"
          cancelText="Cancel"
          confirmColor="#ff6b35"
        />
      </Box>
    </div>
  );
};

export default FaqList;
